import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getTour, DEFAULT_TOUR_NAME } from '../components/UI/tour/tours';

const TourContext = createContext(null);

const TOUR_STORAGE_KEY = 'onboardingTourStep';
const TOUR_ACTIVE_KEY = 'onboardingTourActive';
const TOUR_NAME_KEY = 'onboardingTourName';

// Drop any persisted tour state that survived an auth path which forgot
// to clear it (e.g. an unhandled logout flow). Called both during initial
// rehydration and when an active tour notices the user is unauthenticated.
const clearTourStorage = () => {
  sessionStorage.removeItem(TOUR_STORAGE_KEY);
  sessionStorage.removeItem(TOUR_ACTIVE_KEY);
  sessionStorage.removeItem(TOUR_NAME_KEY);
  sessionStorage.removeItem('onboardingTourGameId');
};

export function TourProvider({ children }) {
  const [isActive, setIsActive] = useState(() => {
    // The engine never auto-rehydrates a tour after a refresh — that's a
    // page-level concern (e.g. the assignments page may opt to resume the
    // assignment-creation tour from sessionStorage if the user is still
    // mid-onboarding). sessionStorage is left intact so those pages can
    // inspect it; we just start the engine inactive on mount.
    return false;
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    const saved = sessionStorage.getItem(TOUR_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [currentTourName, setCurrentTourName] = useState(() => {
    return sessionStorage.getItem(TOUR_NAME_KEY) || DEFAULT_TOUR_NAME;
  });

  // The onComplete handler is passed when startTour is called. It is NOT
  // persisted across reloads — if the tour resumes from sessionStorage after
  // a refresh, the caller (i.e. the page that starts the tour) is expected
  // to re-register the handler on mount if completion persistence matters.
  const onCompleteRef = useRef(null);

  // Optional data bag passed to startTour. Step `body`/`title` functions
  // receive this when rendering, letting pages inject dynamic values
  // (e.g. round count, team count) into static step copy.
  const [stepData, setStepData] = useState(null);

  const [targetRect, setTargetRect] = useState(null);
  const [targetEl, setTargetEl] = useState(null);
  // popoverRect controls where the popover is positioned. Defaults to
  // targetRect but can be overridden per-step via popoverAnchorId.
  const [popoverRect, setPopoverRect] = useState(null);
  // Multi-target steps (step.targetIds: string[]) highlight several
  // elements at once — TourOverlay reads `targetRects` to draw a cutout
  // and ring per element. Single-target steps populate this with one
  // entry mirroring `targetRect` so the overlay only needs one path.
  const [targetRects, setTargetRects] = useState([]);
  const targetElsRef = useRef([]);
  const observerRef = useRef(null);
  const rafRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const timeoutRef = useRef(null);
  const pollRef = useRef(null);
  const popoverAnchorElRef = useRef(null);

  const currentTour = isActive ? getTour(currentTourName) : null;
  const steps = currentTour?.steps || [];
  const currentStep = isActive ? steps[currentStepIndex] : null;
  const totalSteps = steps.length;
  const hideBack = currentTour?.hideBack === true;
  const isReadOnly = currentTour?.readOnly === true;
  const defaultAlign = currentTour?.defaultAlign || 'center';
  // Optional per-tour override for the popover width — TourPopover
  // uses 340px when this is null. Lets a single tour widen the card
  // without bleeding into others.
  const popoverWidth = currentTour?.popoverWidth || null;

  // For read-only tours, highlighted targets should render above the overlay
  // (so they're visible through the cutout) but NOT be clickable. We use a
  // separate attribute for this so CSS can elevate z-index without enabling
  // pointer events.
  const targetAttr = isReadOnly ? 'data-tour-visible-target' : 'data-tour-active-target';

  // Persist state to sessionStorage. Writes are gated on isActive=true
  // so the initial render after a page refresh (when the engine starts
  // inactive) doesn't overwrite a previously persisted tour-paused
  // state with 'false'/'0'/DEFAULT_TOUR_NAME — pages that want to
  // resume from sessionStorage need to read the prior values intact.
  // endTour clears the keys explicitly when the tour actually ends.
  useEffect(() => {
    if (isActive) {
      sessionStorage.setItem(TOUR_STORAGE_KEY, String(currentStepIndex));
    }
  }, [currentStepIndex, isActive]);

  useEffect(() => {
    if (isActive) {
      sessionStorage.setItem(TOUR_ACTIVE_KEY, 'true');
      document.body.classList.add('tour-active');
    } else {
      document.body.classList.remove('tour-active');
      document.body.classList.remove('tour-readonly');
      document.body.removeAttribute('data-tour-name');
      document.body.removeAttribute('data-tour-step-id');
    }
    return () => {
      document.body.classList.remove('tour-active');
      document.body.classList.remove('tour-readonly');
      document.body.removeAttribute('data-tour-name');
      document.body.removeAttribute('data-tour-step-id');
    };
  }, [isActive]);

  // Surface the current tour name + step id as body data attributes so
  // CSS rules can scope styling to specific tours or steps (e.g. sub-
  // highlighting specific rows inside the spotlit column).
  useEffect(() => {
    if (isActive && currentTourName) {
      document.body.setAttribute('data-tour-name', currentTourName);
    } else {
      document.body.removeAttribute('data-tour-name');
    }
  }, [isActive, currentTourName]);

  useEffect(() => {
    const stepId = isActive ? steps[currentStepIndex]?.id : null;
    if (stepId) {
      document.body.setAttribute('data-tour-step-id', stepId);
    } else {
      document.body.removeAttribute('data-tour-step-id');
    }
  }, [isActive, currentStepIndex, steps]);

  useEffect(() => {
    if (isActive && isReadOnly) {
      document.body.classList.add('tour-readonly');
    } else {
      document.body.classList.remove('tour-readonly');
    }
  }, [isActive, isReadOnly]);

  useEffect(() => {
    if (isActive) {
      sessionStorage.setItem(TOUR_NAME_KEY, currentTourName);
    }
  }, [currentTourName, isActive]);

  // Mark the current target element so pointer-events pass through
  // (or so it just renders above the overlay, for read-only tours).
  useEffect(() => {
    if (targetEl) {
      targetEl.setAttribute(targetAttr, 'true');
    }
    return () => {
      if (targetEl) {
        targetEl.removeAttribute(targetAttr);
      }
    };
  }, [targetEl, targetAttr]);

  const updateTargetRect = useCallback((el) => {
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  }, []);

  // Watch for target element appearing in DOM
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isActive || !currentStep) return;

    // Steps may target one element (`targetId`) or several (`targetIds`).
    // Internally we always work with an array; single-target is just a
    // length-1 array. The first entry is the "primary" used for popover
    // anchoring (unless overridden by popoverAnchorId).
    const targetIds = Array.isArray(currentStep.targetIds) && currentStep.targetIds.length
      ? currentStep.targetIds
      : (currentStep.targetId ? [currentStep.targetId] : []);
    const targetId = targetIds[0];
    const popoverAnchorId = currentStep.popoverAnchorId;

    const measureAnchor = (el) => {
      const rect = el.getBoundingClientRect();
      return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };
    };

    const tryFind = () => {
      if (!targetIds.length) return false;
      // Multi-target: require ALL elements present before continuing, so
      // the cutouts and rings come up together rather than one-at-a-time.
      const els = targetIds.map(id => document.querySelector(`[data-tour-id="${id}"]`));
      if (els.some(el => !el)) return false;

      els.forEach(el => el.setAttribute(targetAttr, 'true'));
      targetElsRef.current = els;
      const primary = els[0];
      setTargetEl(primary);
      primary.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

      requestAnimationFrame(() => {
        const rects = els.map(measureAnchor);
        setTargetRects(rects);
        setTargetRect(rects[0]);

        // Popover position: use popoverAnchorId if specified, else the primary target
        if (popoverAnchorId) {
          const anchorEl = document.querySelector(`[data-tour-id="${popoverAnchorId}"]`);
          if (anchorEl) {
            popoverAnchorElRef.current = anchorEl;
            setPopoverRect(measureAnchor(anchorEl));
          } else {
            popoverAnchorElRef.current = primary;
            setPopoverRect(rects[0]);
          }
        } else {
          popoverAnchorElRef.current = primary;
          setPopoverRect(rects[0]);
        }
      });

      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
      if (pollRef.current)     { clearInterval(pollRef.current);   pollRef.current = null; }
      if (timeoutRef.current)  { clearTimeout(timeoutRef.current); timeoutRef.current = null; }

      return true;
    };

    document.querySelectorAll('[data-tour-active-target]').forEach(el => el.removeAttribute('data-tour-active-target'));
    document.querySelectorAll('[data-tour-visible-target]').forEach(el => el.removeAttribute('data-tour-visible-target'));

    setTargetEl(null);
    setTargetRect(null);
    setTargetRects([]);
    setPopoverRect(null);
    targetElsRef.current = [];
    popoverAnchorElRef.current = null;

    // noOverlay steps render a centered popover with no dim/spotlight and
    // no DOM target. Stamp synthetic centered rects so TourPopover has
    // something to position against, and skip the search/observe loop.
    // Optionally accept a `popoverAnchorId` here too — useful when a
    // centered noOverlay step still wants to top-align with a real
    // element (e.g. a column header) without highlighting it.
    if (currentStep.noOverlay) {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const synthetic = { top: cy, left: cx, width: 0, height: 0 };
      setTargetRect(synthetic);

      let anchored = false;
      if (currentStep.popoverAnchorId) {
        const anchorEl = document.querySelector(`[data-tour-id="${currentStep.popoverAnchorId}"]`);
        if (anchorEl) {
          popoverAnchorElRef.current = anchorEl;
          setPopoverRect(measureAnchor(anchorEl));
          anchored = true;
        }
      }
      if (!anchored) setPopoverRect(synthetic);
      return;
    }

    if (currentStep.navigateTo && !window.location.pathname.startsWith(currentStep.navigateTo)) {
      window.location.href = currentStep.navigateTo;
      return;
    }

    const startSearching = () => {
      if (tryFind()) return;

      observerRef.current = new MutationObserver(() => {
        tryFind();
      });
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });

      pollRef.current = setInterval(() => {
        tryFind();
      }, 500);

      // Give up after `notFoundTimeoutMs` (default 5s) and end the tour
      // cleanly. If the target never materializes (page navigation lag,
      // wrong page, etc.) the tour shouldn't strand the user — better to
      // bow out than show a confused popover. Steps that resume after a
      // refresh can shorten this (e.g. 1s for assignment-creation step 1).
      const notFoundTimeoutMs = currentStep.notFoundTimeoutMs || 5000;
      timeoutRef.current = setTimeout(() => {
        if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
        if (pollRef.current)     { clearInterval(pollRef.current);   pollRef.current = null; }
        console.warn(`[Tour] Target "${targetId}" not found after ${notFoundTimeoutMs}ms — ending tour`);
        endTourRef.current();
      }, notFoundTimeoutMs);
    };

    const delay = currentStep.searchDelay || 0;
    if (delay > 0) {
      timeoutRef.current = setTimeout(startSearching, delay);
    } else {
      startSearching();
    }

    return () => {
      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
      if (pollRef.current)     { clearInterval(pollRef.current);   pollRef.current = null; }
      if (timeoutRef.current)  { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      document.querySelectorAll('[data-tour-active-target]').forEach(el => el.removeAttribute('data-tour-active-target'));
      document.querySelectorAll('[data-tour-visible-target]').forEach(el => el.removeAttribute('data-tour-visible-target'));
    };
  }, [isActive, currentStepIndex, targetAttr]);

  // Keep rect updated on scroll/resize
  useEffect(() => {
    if (!isActive || !targetEl) return;

    const handleUpdate = () => {
      rafRef.current = requestAnimationFrame(() => {
        updateTargetRect(targetEl);
        // Refresh every multi-target rect so all cutouts/rings stay
        // aligned with their elements during scroll/resize.
        const els = targetElsRef.current;
        if (els && els.length) {
          setTargetRects(els.map(el => {
            const r = el.getBoundingClientRect();
            return { top: r.top, left: r.left, width: r.width, height: r.height };
          }));
        }
        const anchor = popoverAnchorElRef.current;
        if (anchor) {
          const rect = anchor.getBoundingClientRect();
          setPopoverRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      });
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    resizeObserverRef.current = new ResizeObserver(handleUpdate);
    resizeObserverRef.current.observe(targetEl);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, [isActive, targetEl, updateTargetRect]);

  // Start a named tour. `onComplete` is called when the tour ends (skip
  // or finish); use it to persist completion state (e.g. PATCH a user flag).
  const startTour = useCallback((name = DEFAULT_TOUR_NAME, { onComplete, data } = {}) => {
    const tour = getTour(name);
    if (!tour) {
      console.warn(`[Tour] Unknown tour name: "${name}"`);
      return;
    }
    onCompleteRef.current = onComplete || null;
    setStepData(data || null);
    setCurrentTourName(name);
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
    setTargetRect(null);
    setTargetRects([]);
    setTargetEl(null);
    targetElsRef.current = [];
    setStepData(null);
    sessionStorage.removeItem(TOUR_STORAGE_KEY);
    sessionStorage.removeItem(TOUR_ACTIVE_KEY);
    sessionStorage.removeItem(TOUR_NAME_KEY);
    sessionStorage.removeItem('onboardingTourGameId');

    // Fire the per-tour completion handler, if registered
    const onComplete = onCompleteRef.current;
    onCompleteRef.current = null;
    if (typeof onComplete === 'function') {
      try {
        const result = onComplete();
        if (result && typeof result.catch === 'function') {
          result.catch((err) => console.error('[Tour] onComplete failed:', err));
        }
      } catch (err) {
        console.error('[Tour] onComplete threw:', err);
      }
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      endTour();
    }
  }, [currentStepIndex, totalSteps, endTour]);

  // Used by the target-search timeout to auto-advance past steps whose
  // targets never appear, so the tour doesn't strand the user (e.g.,
  // student tournament tour's user-team step when their team isn't in
  // the current round). Wrapped in a ref so the search effect can call
  // it without taking on `nextStep` as a dep and re-running on every step.
  const nextStepRef = useRef(nextStep);
  useEffect(() => {
    nextStepRef.current = nextStep;
  }, [nextStep]);

  // Ref-wrapped endTour for the same reason — the find-target effect
  // calls it from the 5s "target not found" timeout to end the tour
  // cleanly, without taking endTour as a dep that would re-run on every
  // step change.
  const endTourRef = useRef(endTour);
  useEffect(() => {
    endTourRef.current = endTour;
  }, [endTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((n) => {
    if (n >= 0 && n < totalSteps) {
      setCurrentStepIndex(n);
    }
  }, [totalSteps]);

  const value = {
    isActive,
    currentTourName,
    currentStepIndex,
    currentStep,
    totalSteps,
    targetRect,
    targetRects,
    targetEl,
    popoverRect,
    stepData,
    hideBack,
    defaultAlign,
    popoverWidth,
    startTour,
    endTour,
    nextStep,
    prevStep,
    goToStep,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
